import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  UploadCloud, CheckCircle2, Clock, Loader2, Trash2, FileText,
  Hash, CalendarDays, History, ShieldCheck, X, RefreshCw, Layers, Building2, FileStack, Pencil,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../utils/axios';

const MESES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];

const fmtNum = (n) => (n == null ? '—' : Number(n).toLocaleString('es-AR'));
const fmtCuit = (c) => {
  if (!c) return 'Sin CUIT';
  const d = String(c).replace(/\D/g, '');
  return d.length === 11 ? `${d.slice(0, 2)}-${d.slice(2, 10)}-${d.slice(10)}` : c;
};
const keyOf = (c) => c.cuit || 'SIN-CUIT';

export default function PresentacionesTab() {
  const [data, setData] = useState({ anios: [], cuits: [], total_presentaciones: 0, total_cuits: 0 });
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [selectedCuit, setSelectedCuit] = useState(null);
  const [selectedForm, setSelectedForm] = useState(null);
  const [year, setYear] = useState(null);
  const [detail, setDetail] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [edit, setEdit] = useState(null); // {type:'cuit'|'form', cuit?, formulario?, label, value}
  const [savingEdit, setSavingEdit] = useState(false);
  const inputRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/reporte-f8126/presentaciones');
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudieron cargar las presentaciones');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const subirArchivos = useCallback(async (fileList) => {
    const pdfs = Array.from(fileList).filter((f) => f.name.toLowerCase().endsWith('.pdf'));
    if (!pdfs.length) {
      toast.error('Soltá archivos PDF (acuses de AFIP).');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      pdfs.forEach((f) => formData.append('files', f));
      const res = await api.post('/reporte-f8126/presentaciones/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const r = res.data;
      if (r.ok > 0) toast.success(`${r.ok} acuse(s) leído(s) y catalogado(s) por CUIT y formulario.`);
      if (r.duplicados > 0) toast.info(`${r.duplicados} acuse(s) ya estaban cargados.`);
      if (r.errores > 0) {
        const primerError = (r.resultados || []).find((x) => x.estado === 'error');
        toast.error(`${r.errores} archivo(s) no se pudieron leer${primerError ? `: ${primerError.mensaje}` : ''}`);
      }
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al subir los acuses');
    } finally {
      setUploading(false);
    }
  }, [load]);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    if (e.dataTransfer.files?.length) subirArchivos(e.dataTransfer.files);
  };

  // ---- Derivados (con fallback al primero, sin efectos complejos) ----
  const esAdmin = !!data?.es_admin;
  const cuits = data.cuits || [];
  const cuitSel = cuits.find((c) => keyOf(c) === selectedCuit) || cuits[0] || null;
  const forms = cuitSel?.formularios || [];
  const formSel = forms.find((f) => f.formulario === selectedForm) || forms[0] || null;
  const periodos = formSel?.periodos || [];
  const yearsForm = Array.from(new Set([...(formSel?.anios || []), ...(data.anios || [])]))
    .filter(Boolean).sort((a, b) => b - a);
  const yearEff = yearsForm.includes(year) ? year : (periodos[0]?.anio || yearsForm[0] || new Date().getFullYear());

  const porMes = {};
  periodos.forEach((p) => { if (p.anio === yearEff) porMes[p.mes] = p; });
  const presentadosAnio = Object.keys(porMes).length;

  const saveEdit = async () => {
    if (!edit) return;
    try {
      setSavingEdit(true);
      if (edit.type === 'cuit') {
        await api.put('/reporte-f8126/presentaciones/cuit-nombre', { cuit: edit.cuit, nombre: (edit.value || '').trim() });
        toast.success('Nombre del CUIT actualizado');
      } else {
        await api.put('/reporte-f8126/presentaciones/formulario-alias', { formulario: edit.formulario, alias: (edit.value || '').trim() });
        toast.success('Alias del formulario actualizado');
      }
      setEdit(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo guardar');
    } finally {
      setSavingEdit(false);
    }
  };

  const confirmarBorrado = async () => {
    if (!deleteTarget) return;
    try {
      const params = {};
      if (cuitSel?.cuit) params.cuit = cuitSel.cuit;
      if (formSel?.formulario) params.formulario = formSel.formulario;
      await api.delete(`/reporte-f8126/presentaciones/periodo/${deleteTarget.periodo}`, { params });
      toast.success(`Se quitó ${deleteTarget.mes_nombre} ${deleteTarget.anio} del tablero.`);
      setDeleteTarget(null);
      setDetail(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo eliminar');
    }
  };

  return (
    <div className="space-y-6" data-testid="presentaciones-tab">
      {/* Zona de Drag & Drop (solo admin) */}
      {esAdmin && (
      <div
        data-testid="presentaciones-dropzone"
        onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
        onDragLeave={(e) => { e.preventDefault(); setDragActive(false); }}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
        className={`relative rounded-2xl border-2 border-dashed cursor-pointer transition-colors duration-200 p-10 text-center
          ${dragActive ? 'border-indigo-500 bg-indigo-50' : 'border-slate-300 bg-slate-50 hover:border-indigo-400 hover:bg-indigo-50/40'}`}
      >
        <input
          ref={inputRef}
          type="file"
          accept="application/pdf,.pdf"
          multiple
          className="hidden"
          data-testid="presentaciones-file-input"
          onChange={(e) => { if (e.target.files?.length) subirArchivos(e.target.files); e.target.value = ''; }}
        />
        <div className="flex flex-col items-center gap-3 pointer-events-none">
          <div className={`w-16 h-16 rounded-full flex items-center justify-center ${dragActive ? 'bg-indigo-600' : 'bg-indigo-100'}`}>
            {uploading
              ? <Loader2 size={30} className="text-indigo-600 animate-spin" />
              : <UploadCloud size={30} className={dragActive ? 'text-white' : 'text-indigo-600'} />}
          </div>
          <div>
            <p className="text-lg font-semibold text-slate-800">
              {uploading ? 'Leyendo acuses…' : 'Arrastrá los acuses de AFIP (PDF) acá'}
            </p>
            <p className="text-sm text-slate-500 mt-1">
              Detecta automáticamente el formulario (F8125, F8126, etc.) y el CUIT, y los cataloga. Los PDF no se almacenan.
            </p>
          </div>
          {!uploading && (
            <Badge variant="outline" className="mt-1 pointer-events-none">o hacé clic para elegir archivos · varios CUIT permitidos</Badge>
          )}
        </div>
      </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20 text-slate-400">
          <Loader2 size={28} className="animate-spin mr-2" /> Cargando tablero…
        </div>
      ) : cuits.length === 0 ? (
        <Card className="p-10 text-center border-dashed border-2 border-slate-200" data-testid="presentaciones-empty">
          <FileStack className="h-10 w-10 mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500">No hay presentaciones cargadas.</p>
          {esAdmin && (
            <p className="text-sm text-slate-400 mt-1">Soltá los PDF de AFIP arriba y se catalogarán solos por CUIT y formulario.</p>
          )}
        </Card>
      ) : (
        <>
          {/* Selector de CUIT */}
          <div data-testid="presentaciones-cuit-selector">
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2 flex items-center gap-1.5">
              <Building2 size={13} /> CUIT informante
            </p>
            <div className="flex gap-3 flex-wrap">
              {cuits.map((c) => {
                const activo = keyOf(c) === keyOf(cuitSel);
                return (
                  <div
                    key={keyOf(c)}
                    role="button"
                    tabIndex={0}
                    data-testid={`presentaciones-cuit-${keyOf(c)}`}
                    onClick={() => { setSelectedCuit(keyOf(c)); setSelectedForm(null); setYear(null); }}
                    className={`relative text-left rounded-xl border px-4 py-3 min-w-[200px] cursor-pointer transition-all duration-150
                      ${activo
                        ? 'border-indigo-500 bg-indigo-50 ring-2 ring-indigo-500/20 shadow-sm'
                        : 'border-slate-200 bg-white hover:border-indigo-300 hover:bg-slate-50'}`}
                  >
                    {esAdmin && c.cuit && (
                      <button
                        data-testid={`presentaciones-cuit-edit-${keyOf(c)}`}
                        onClick={(e) => { e.stopPropagation(); setEdit({ type: 'cuit', cuit: c.cuit, label: fmtCuit(c.cuit), value: c.nombre || '' }); }}
                        className="absolute top-2 right-2 p-1 rounded-md text-slate-300 hover:text-indigo-600 hover:bg-white"
                        title="Ponerle un nombre a este CUIT"
                      >
                        <Pencil size={13} />
                      </button>
                    )}
                    <p className={`font-semibold truncate max-w-[220px] pr-5 ${activo ? 'text-indigo-900' : 'text-slate-800'}`}>
                      {c.denominacion || fmtCuit(c.cuit)}
                    </p>
                    <div className="flex items-center justify-between gap-2 mt-1">
                      <span className="text-xs font-mono text-slate-400">{fmtCuit(c.cuit)}</span>
                      <Badge className={`text-[10px] px-1.5 py-0 ${activo ? 'bg-indigo-600 text-white hover:bg-indigo-600' : 'bg-slate-100 text-slate-500 hover:bg-slate-100'}`}>
                        {c.total} envío{c.total === 1 ? '' : 's'}
                      </Badge>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Sub-tabs de formulario + resumen + año */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4" data-testid="presentaciones-summary">
            <div className="flex items-center gap-2 flex-wrap" data-testid="presentaciones-form-tabs">
              {forms.map((f) => {
                const activo = f.formulario === formSel?.formulario;
                return (
                  <div key={f.formulario} className="flex items-center">
                    <button
                      data-testid={`presentaciones-form-${f.formulario}`}
                      onClick={() => { setSelectedForm(f.formulario); setYear(null); }}
                      className={`px-3.5 py-2 rounded-lg text-sm font-semibold border transition-colors flex items-center gap-2
                        ${activo
                          ? 'bg-slate-900 text-white border-slate-900'
                          : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'}`}
                    >
                      <FileText size={14} className={activo ? 'text-white' : 'text-slate-400'} />
                      {f.label}{f.alias ? <span className={`font-normal ${activo ? 'text-white/80' : 'text-slate-400'}`}>· {f.alias}</span> : null}
                      <span className={`text-[10px] rounded-full px-1.5 ${activo ? 'bg-white/20' : 'bg-slate-100 text-slate-500'}`}>{f.total}</span>
                    </button>
                    {esAdmin && activo && (
                      <button
                        data-testid={`presentaciones-form-edit-${f.formulario}`}
                        onClick={() => setEdit({ type: 'form', formulario: f.formulario, label: f.label, value: f.alias || '' })}
                        className="ml-1 p-1.5 rounded-md text-slate-400 hover:text-indigo-600 hover:bg-slate-100"
                        title="Agregarle un alias a este formulario"
                      >
                        <Pencil size={13} />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-lg bg-emerald-100 flex items-center justify-center">
                  <CheckCircle2 size={18} className="text-emerald-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500 leading-tight">Presentados {yearEff}</p>
                  <p className="text-lg font-bold text-slate-800 leading-tight" data-testid="presentaciones-presentados-anio">{presentadosAnio} / 12</p>
                </div>
              </div>
              <Button variant="outline" size="sm" onClick={load} data-testid="presentaciones-refresh-btn">
                <RefreshCw size={15} className="mr-1.5" /> Actualizar
              </Button>
              <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1">
                {yearsForm.map((a) => (
                  <button
                    key={a}
                    data-testid={`presentaciones-year-${a}`}
                    onClick={() => setYear(a)}
                    className={`px-3 py-1.5 text-sm font-semibold rounded-md transition-colors
                      ${a === yearEff ? 'bg-indigo-600 text-white shadow-sm' : 'text-slate-600 hover:bg-white'}`}
                  >
                    {a}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Grilla de 12 meses */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="presentaciones-grid">
            {MESES.map((nombreMes, idx) => {
              const mes = idx + 1;
              const p = porMes[mes];
              const presentado = !!p;
              return (
                <Card
                  key={mes}
                  data-testid={`presentacion-card-${yearEff}-${mes}`}
                  onClick={() => presentado && setDetail(p)}
                  className={`relative p-4 border transition-all duration-200 overflow-hidden
                    ${presentado
                      ? 'bg-emerald-50 border-emerald-200 hover:border-emerald-400 hover:shadow-md cursor-pointer'
                      : 'bg-white border-slate-200'}`}
                >
                  <div className={`absolute top-0 left-0 h-full w-1.5 ${presentado ? 'bg-emerald-500' : 'bg-slate-200'}`} />
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-xs font-medium text-slate-400 uppercase tracking-wide">{yearEff}</p>
                      <p className="text-lg font-bold text-slate-800">{nombreMes}</p>
                    </div>
                    {presentado
                      ? <CheckCircle2 size={22} className="text-emerald-500 shrink-0" />
                      : <Clock size={20} className="text-slate-300 shrink-0" />}
                  </div>

                  {presentado ? (
                    <div className="mt-3 space-y-1.5">
                      <Badge data-testid="pres-badge-presentado" className="bg-emerald-600 hover:bg-emerald-600 text-white">Presentado</Badge>
                      <div className="flex items-center gap-1.5 text-xs text-slate-600">
                        <Hash size={12} className="text-slate-400" />
                        <span className="font-mono truncate">{p.nro_transaccion || '—'}</span>
                      </div>
                      <div className="flex items-center gap-1.5 text-xs text-slate-600">
                        <CalendarDays size={12} className="text-slate-400" />
                        <span>{p.fecha_presentacion || '—'}</span>
                      </div>
                      <div className="flex items-center gap-2 pt-0.5">
                        {p.rectificativa > 0 && (
                          <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-700 bg-amber-50">
                            Rectif. {p.rectificativa}
                          </Badge>
                        )}
                        {p.cantidad_presentaciones > 1 && (
                          <span className="text-[10px] text-slate-400 flex items-center gap-1">
                            <History size={11} /> {p.cantidad_presentaciones} envíos
                          </span>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="mt-3">
                      <Badge variant="outline" className="text-slate-400 border-slate-200">Pendiente</Badge>
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        </>
      )}

      {/* Modal de detalle */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-lg" data-testid="presentacion-detail-modal">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <ShieldCheck size={20} className="text-emerald-600" />
                  {detail.mes_nombre} {detail.anio} — Presentado
                </DialogTitle>
                <DialogDescription>
                  {detail.formulario_label || 'Acuse AFIP'}{formSel?.alias ? ` · ${formSel.alias}` : ''} {detail.version ? `(${detail.version})` : ''} · Período {detail.periodo_iso}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <DetalleItem icon={<FileText size={14} />} label="Formulario" value={detail.formulario_label} />
                  <DetalleItem icon={<Hash size={14} />} label="Nro. de Transacción" value={detail.nro_transaccion} mono />
                  <DetalleItem icon={<CalendarDays size={14} />} label="Fecha de presentación" value={`${detail.fecha_presentacion || '—'} ${detail.hora_presentacion || ''}`} />
                  <DetalleItem icon={<FileText size={14} />} label="Cantidad de registros" value={fmtNum(detail.cantidad_registros)} />
                  <DetalleItem icon={<ShieldCheck size={14} />} label="Código de control" value={detail.codigo_control} mono />
                  <DetalleItem icon={<Layers size={14} />} label="Rectificativa" value={String(detail.rectificativa ?? 0)} />
                  <DetalleItem icon={<Building2 size={14} />} label="CUIT informante" value={fmtCuit(detail.cuit_informante)} mono />
                </div>
                {detail.nombre_archivo_txt && (
                  <div className="text-xs text-slate-500 bg-slate-50 rounded-md p-2 font-mono break-all">
                    {detail.nombre_archivo_txt}
                  </div>
                )}

                {detail.historial && detail.historial.length > 1 && (
                  <div>
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5 flex items-center gap-1">
                      <History size={13} /> Historial de presentaciones ({detail.historial.length})
                    </p>
                    <div className="space-y-1.5 max-h-40 overflow-y-auto">
                      {detail.historial.map((h) => (
                        <div key={h.id} className="flex items-center justify-between text-xs bg-slate-50 rounded-md px-2.5 py-1.5">
                          <span className="flex items-center gap-2">
                            <Badge variant="outline" className="text-[10px]">Rectif. {h.rectificativa ?? 0}</Badge>
                            <span className="font-mono text-slate-600">{h.nro_transaccion}</span>
                          </span>
                          <span className="text-slate-400">{h.fecha_presentacion}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="flex justify-between items-center pt-2">
                {esAdmin ? (
                  <Button
                    variant="ghost"
                    className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                    onClick={() => { setDeleteTarget(detail); setDetail(null); }}
                    data-testid="presentacion-delete-btn"
                  >
                    <Trash2 size={15} className="mr-1.5" /> Quitar del tablero
                  </Button>
                ) : <span />}
                <Button variant="outline" onClick={() => setDetail(null)} data-testid="presentacion-detail-close-btn">
                  <X size={15} className="mr-1.5" /> Cerrar
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Confirmación de borrado */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="presentacion-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Quitar esta presentación?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget && (
                <>Se quitará <strong>{deleteTarget.formulario_label} · {deleteTarget.mes_nombre} {deleteTarget.anio}</strong> del tablero
                {deleteTarget.cantidad_presentaciones > 1 ? ` (incluye ${deleteTarget.cantidad_presentaciones} envíos del período)` : ''}.
                Esta acción no borra nada en AFIP, solo el registro visual acá.</>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="presentacion-delete-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmarBorrado}
              className="bg-rose-600 hover:bg-rose-700"
              data-testid="presentacion-delete-confirm"
            >
              Quitar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Diálogo de edición: nombre de CUIT / alias de formulario */}
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="max-w-md" data-testid="presentaciones-edit-dialog">
          {edit && (
            <>
              <DialogHeader>
                <DialogTitle>
                  {edit.type === 'cuit' ? 'Nombre del CUIT' : 'Alias del formulario'}
                </DialogTitle>
                <DialogDescription>
                  {edit.type === 'cuit'
                    ? <>Poné un nombre para el CUIT <span className="font-mono">{edit.label}</span>. Se mostrará en grande y el CUIT quedará abajo.</>
                    : <>Agregá un alias que se verá al lado de <strong>{edit.label}</strong> (ej. el régimen que informa).</>}
                </DialogDescription>
              </DialogHeader>
              <div className="py-1">
                <Input
                  autoFocus
                  value={edit.value}
                  onChange={(e) => setEdit({ ...edit, value: e.target.value })}
                  onKeyDown={(e) => { if (e.key === 'Enter') saveEdit(); }}
                  placeholder={edit.type === 'cuit' ? 'Ej. Waya S.A.' : 'Ej. Ganancias PSP'}
                  data-testid="presentaciones-edit-input"
                />
                <p className="text-[11px] text-slate-400 mt-1.5">Dejalo vacío para quitar {edit.type === 'cuit' ? 'el nombre' : 'el alias'}.</p>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setEdit(null)} data-testid="presentaciones-edit-cancel">Cancelar</Button>
                <Button onClick={saveEdit} disabled={savingEdit} data-testid="presentaciones-edit-save">
                  {savingEdit ? <Loader2 className="animate-spin mr-1.5" size={14} /> : null}
                  Guardar
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DetalleItem({ icon, label, value, mono }) {
  return (
    <div className="bg-slate-50 rounded-lg p-2.5">
      <p className="text-[11px] text-slate-400 flex items-center gap-1 mb-0.5">{icon} {label}</p>
      <p className={`text-sm font-semibold text-slate-800 break-all ${mono ? 'font-mono' : ''}`}>{value || '—'}</p>
    </div>
  );
}
